import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Sidebar from '../components/Sidebar';
import '../styles/OwnerDashboard.css';

export default function OwnerDashboard() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    suspendedCompanies: 0,
    totalDeals: 0,
    completedDeals: 0,
    totalCommissions: 0,
    monthlyRevenue: 0,
    payingCompanies: 0,
    trialingRevenue: 0
  });
  const [topCompanies, setTopCompanies] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await fetch('/api/owner/overview', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOverview(data.overview || {});
        setTopCompanies(data.topCompanies || []);
      }

      // BrokerConnect's own sales pipeline (broker prospects)
      try {
        const pRes = await fetch('/api/prospects', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (pRes.ok) setProspects(await pRes.json());
      } catch {}
    } catch (err) {
      setError('Failed to load dashboard');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  const companyStatusData = [
    { name: 'Active', value: overview.activeCompanies },
    { name: 'Trialing', value: overview.trialingCompanies || 0 },
    { name: 'Suspended', value: overview.suspendedCompanies }
  ].filter(d => d.value > 0);

  const dealStatusData = [
    { name: 'Pipeline', value: overview.totalDeals - overview.completedDeals },
    { name: 'Completed', value: overview.completedDeals }
  ];

  const COLORS = { Active: '#10b981', Trialing: '#f59e0b', Suspended: '#ef4444' };

  // Prospect funnel rollup
  const PROSPECT_STAGES = ['identified', 'contacted', 'demo', 'negotiating', 'converted', 'lost'];
  const prospectCounts = PROSPECT_STAGES.reduce((acc, st) => {
    acc[st] = prospects.filter(p => p.stage === st).length;
    return acc;
  }, {});
  const overdueFollowUps = prospects.filter(p =>
    p.follow_up_date && new Date(p.follow_up_date) < new Date() && !['converted', 'lost'].includes(p.stage)
  ).length;
  const openProspects = prospects.filter(p => !['converted', 'lost'].includes(p.stage)).length;

  return (
    <>
      <div className="dashboard-container">
        <Sidebar />
        <main className="dashboard-main">
          <header className="dashboard-header">
            <h1>Platform Dashboard</h1>
            <p>Overview of all broker companies and deals</p>
          </header>

          {error && <div className="error-message">{error}</div>}

          <section className="metrics-grid">
            <div className="metric-card">
              <h3>Total Companies</h3>
              <p className="metric-value">{overview.totalCompanies}</p>
              <p className="metric-label">{overview.activeCompanies} active{overview.trialingCompanies ? ` · ${overview.trialingCompanies} trialing` : ''}</p>
            </div>

            <div className="metric-card">
              <h3>Total Deals</h3>
              <p className="metric-value">{overview.totalDeals}</p>
              <p className="metric-label">{overview.completedDeals} completed</p>
            </div>

            <div className="metric-card">
              <h3>Total Commissions</h3>
              <p className="metric-value">£{overview.totalCommissions.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</p>
              <p className="metric-label">across all deals</p>
            </div>

            <div className="metric-card">
              <h3>Monthly Recurring Revenue</h3>
              <p className="metric-value">£{(overview.monthlyRevenue || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              <p className="metric-label">{overview.payingCompanies || 0} paying {(overview.payingCompanies || 0) === 1 ? 'company' : 'companies'}{(overview.trialingRevenue || 0) > 0 ? ` · £${(overview.trialingRevenue || 0).toFixed(2)} trialing` : ''}</p>
            </div>
          </section>

          {/* BrokerConnect sales pipeline — broker prospects */}
          <section style={{ marginBottom: 24 }}>
            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, padding: '20px 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0e1b2c' }}>
                  Broker Prospects <span style={{ fontSize: 12, fontWeight: 400, color: '#a0aec0' }}>BrokerConnect sales pipeline</span>
                </h2>
                <button onClick={() => navigate('/owner/prospects')} style={{ background: 'none', border: '1px solid #CBD5E0', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 600, color: '#4a5568', cursor: 'pointer' }}>
                  Manage prospects →
                </button>
              </div>
              {prospects.length === 0 ? (
                <p style={{ margin: 0, fontSize: 13, color: '#a0aec0' }}>No prospects yet — website enquiries land here automatically.</p>
              ) : (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Open', value: openProspects, color: '#0e1b2c', bg: '#f8fafc' },
                    { label: 'Identified', value: prospectCounts.identified, color: '#d97706', bg: '#fffbeb' },
                    { label: 'Contacted', value: prospectCounts.contacted, color: '#2563eb', bg: '#eff6ff' },
                    { label: 'Demo', value: prospectCounts.demo, color: '#7c3aed', bg: '#f5f3ff' },
                    { label: 'Negotiating', value: prospectCounts.negotiating, color: '#0f766e', bg: '#f0fdfa' },
                    { label: 'Converted', value: prospectCounts.converted, color: '#059669', bg: '#f0fdf4' },
                    { label: 'Overdue follow-ups', value: overdueFollowUps, color: overdueFollowUps > 0 ? '#dc2626' : '#a0aec0', bg: overdueFollowUps > 0 ? '#fee2e2' : '#f8fafc' },
                  ].map(c => (
                    <div key={c.label} onClick={() => navigate('/owner/prospects')} style={{ flex: '1 1 110px', minWidth: 110, background: c.bg, borderRadius: 8, padding: '12px 14px', cursor: 'pointer' }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: c.color }}>{c.value || 0}</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#718096', marginTop: 2 }}>{c.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="charts-section">
            <div className="chart-container">
              <h2>Company Status</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={companyStatusData} cx="50%" cy="50%" labelLine={false} label={({ name, value }) => `${name}: ${value}`} outerRadius={80} fill="#8884d8" dataKey="value">
                    {companyStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[entry.name] || '#94a3b8'} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-container">
              <h2>Deal Status</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={dealStatusData} cx="50%" cy="50%" labelLine={false} label={({ name, value }) => `${name}: ${value}`} outerRadius={80} fill="#8884d8" dataKey="value">
                    {dealStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={index === 0 ? '#3b82f6' : '#10b981'} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </section>

          {topCompanies.length > 0 && (
            <section className="top-companies">
              <h2>Top Companies by Deals</h2>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={topCompanies}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="deal_count" fill="#10b981" name="Deal Count" />
                  <Bar dataKey="total_commission" fill="#84cc16" name="Total Commission" />
                </BarChart>
              </ResponsiveContainer>
            </section>
          )}
        </main>
      </div>
    </>
  );
}
